import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import { removePartnerNdcPreferenceApi } from 'api/inventory';
import { getGraphQLErrorMessage } from 'common/utils/i18nErrorTranslations';
import PreferenceContextPanel from './PreferenceContextPanel';
import { catalogName } from '../utils/formatters';

const RemovePartnerPreferenceDialog = ({ open, partner, preference, onClose, onRemoved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) setError('');
  }, [open]);

  const drugLabel = catalogName(preference ?? {});

  const handleRemove = () => {
    setRemoving(true);
    setError('');

    removePartnerNdcPreferenceApi({
      partnerUuid: partner?.partnerId,
      productId: preference?.productId,
    })
      .then((removed) => {
        // The mutation returns false rather than failing when there was no active
        // preference left to clear — someone else got there first. Saying
        // "removed" would be a lie, but the list is stale either way, so refresh.
        enqueueSnackbar(
          removed
            ? `Preference removed for ${drugLabel}`
            : 'That preference had already been removed',
          { variant: removed ? 'success' : 'info' }
        );
        onRemoved?.({ partnerUuid: partner?.partnerId });
        onClose?.();
      })
      .catch((removeError) => {
        setError(getGraphQLErrorMessage(removeError));
      })
      .finally(() => setRemoving(false));
  };

  return (
    <Dialog open={open} onClose={removing ? undefined : onClose} fullWidth maxWidth='xs'>
      <DialogTitle sx={{ fontWeight: 700 }}>Remove preference?</DialogTitle>
      <DialogContent dividers>
        <PreferenceContextPanel
          partner={partner}
          drug={preference}
          currentNdc={preference?.ndc ?? ''}
          currentManufacturer={preference?.manufacturer ?? ''}
        />

        {/* States the outcome rather than asking "are you sure": removing a
            preference changes live allocation behaviour, it does not just delete a
            row. */}
        <Typography variant='body2'>
          Orders from <strong>{partner?.partnerName}</strong> for {drugLabel} will go back to being
          filled from <strong>any available NDC</strong> under normal allocation.
        </Typography>
        {error && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={removing}>
          Cancel
        </Button>
        <Button
          variant='contained'
          color='error'
          onClick={handleRemove}
          disabled={removing}
          startIcon={removing ? <CircularProgress size={16} color='inherit' /> : null}
        >
          Remove
        </Button>
      </DialogActions>
    </Dialog>
  );
};

RemovePartnerPreferenceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  partner: PropTypes.shape({
    partnerId: PropTypes.string,
    partnerName: PropTypes.string,
  }),
  preference: PropTypes.shape({
    productId: PropTypes.string,
    drugName: PropTypes.string,
    strength: PropTypes.string,
    dosageForm: PropTypes.string,
    formAlias: PropTypes.string,
    packSizeValue: PropTypes.string,
    packSizeUnit: PropTypes.string,
    manufacturer: PropTypes.string,
    ndc: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onRemoved: PropTypes.func.isRequired,
};

RemovePartnerPreferenceDialog.defaultProps = {
  partner: null,
  preference: null,
};

export default RemovePartnerPreferenceDialog;
